
import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Compass, Calendar, User, Sparkles, Heart } from 'lucide-react';
import HomePage from './pages/HomePage';

// Lazy load Agentation only in DEV to prevent bundling in production
const Agentation = lazy(() =>
  import('agentation').then(mod => ({ default: mod.Agentation }))
);
import AboutPage from './pages/AboutPage';
import WorkshopsPage from './pages/WorkshopsPage';
import ImpactPage from './pages/ImpactPage';
import ApproachPage from './pages/ApproachPage';
import ProcessPage from './pages/ProcessPage';
import HowIHelpPage from './pages/HowIHelpPage';
import { Language, View } from './types';
import { useScrolled } from './hooks/useScrolled';
import { cn } from './lib/utils';
import { pageStackVariants } from './lib/fluid-animations';
import ScrollProgress from './components/ui/ScrollProgress';

// Lazy load the heavy Journey component
const JourneyPage = lazy(() => import('./pages/JourneyPage'));

const Nav: React.FC<{ lang: Language; setLang: (l: Language) => void; view: View; setView: (v: View) => void }> = ({ lang, setLang, view, setView }) => {
  const toggleLang = () => setLang(lang === 'en' ? 'fa' : 'en');
  const scrolled = useScrolled(50);

  // Desktop Links with icons
  const desktopLinks = [
    { label: { en: 'Journey', fa: 'سفر درونی' }, onClick: () => setView('journey'), icon: Compass },
    { label: { en: 'About', fa: 'درباره من' }, onClick: () => setView('about'), icon: User },
    { label: { en: 'Process', fa: 'فرایند' }, onClick: () => setView('how-i-help'), icon: Heart },
  ];

  // Mobile Bottom Nav Links
  const mobileLinks = [
    { id: 'journey', label: { en: 'Journey', fa: 'سفر' }, icon: Compass, view: 'journey' as View },
    { id: 'about', label: { en: 'About', fa: 'درباره' }, icon: User, view: 'about' as View },
    { id: 'how-i-help', label: { en: 'Process', fa: 'فرایند' }, icon: Sparkles, view: 'how-i-help' as View },
  ];

  const handleLogoClick = () => {
    setView('home');
    if (window.location.hash) {
      history.pushState("", document.title, window.location.pathname + window.location.search);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      {/* Top Header - Mobile: full glass bar. Desktop: transparent initially, blur bar after scroll */}
      <header
        className={cn(
          "fixed top-0 left-0 w-full z-50 px-6 py-3 md:px-12 md:py-4 flex justify-between items-center",
          // Smooth transitions for all properties including shadow
          "transition-[background-color,backdrop-filter,border-color,box-shadow] duration-300 ease-out",
          // Mobile: always show glass bar with subtle shadow
          "bg-white/80 backdrop-blur-xl border-b border-white/20 shadow-sm",
          // Desktop: conditional glass bar with smooth shadow transition
          scrolled
            ? "md:bg-white/70 md:backdrop-blur-xl md:border-b md:border-white/20 md:shadow-md"
            : "md:bg-transparent md:backdrop-blur-none md:border-b-0 md:shadow-none"
        )}
        dir="ltr"
      >
        {/* Logo */}
        <button
          onClick={handleLogoClick}
          className="btn btn-ghost text-xl md:text-2xl font-bold tracking-tight text-brandDark hover:text-brandDark/80 transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none rounded-lg"
          aria-label={lang === 'en' ? 'Kayvan Coaching - Go to homepage' : 'کیوان کوچینگ - رفتن به صفحه اصلی'}
        >
          kayvan <span className="text-primary">coaching</span>
        </button>

        {/* Desktop Navigation - integrated with header blur */}
        <nav className="hidden md:flex items-center gap-2" aria-label="Main navigation">
          {desktopLinks.map((l, i) => {
            const Icon = l.icon;
            return (
              <button
                key={i}
                onClick={l.onClick}
                className={cn(
                  "group flex items-center gap-3 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer",
                  "text-gray-600 hover:text-brandDark hover:bg-white/80 hover:shadow-sm",
                  "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none",
                  lang === 'fa' ? 'font-farsi' : ''
                )}
              >
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                  <Icon className="w-4 h-4 text-primary" aria-hidden="true" />
                </span>
                <span>{l.label[lang]}</span>
              </button>
            );
          })}
          <div className="h-8 w-px mx-2 bg-gray-200/60" />
          <button
            onClick={toggleLang}
            aria-label={lang === 'en' ? 'Switch to Persian' : 'Switch to English'}
            className={cn(
              "px-4 py-2.5 rounded-xl text-sm font-medium text-gray-500 hover:text-brandDark hover:bg-white/80 hover:shadow-sm transition-all duration-200 cursor-pointer",
              "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none",
              lang === 'fa' ? 'font-farsi' : ''
            )}
          >
            {lang === 'en' ? 'فارسی' : 'EN'}
          </button>
        </nav>

        {/* Mobile Language Toggle (Top Right) */}
        <div className="md:hidden">
           <button
            onClick={toggleLang}
            aria-label={lang === 'en' ? 'Switch to Persian' : 'Switch to English'}
            className="min-h-[44px] min-w-[44px] px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-brandDark hover:bg-white/50 transition-all active:scale-95 cursor-pointer focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            {lang === 'en' ? 'FA' : 'EN'}
          </button>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav
        className="md:hidden fixed bottom-0 left-0 w-full z-50 bg-white/95 backdrop-blur-xl border-t border-gray-200/60 shadow-[0_-4px_16px_rgba(0,0,0,0.04)] pb-safe pt-2 px-4 flex justify-around items-center"
        aria-label={lang === 'en' ? 'Mobile navigation' : 'ناوبری موبایل'}
      >
        {mobileLinks.map((item) => (
          <button
            key={item.id}
            onClick={() => setView(item.view)}
            aria-current={view === item.view ? 'page' : undefined}
            className={cn(
              "btn btn-ghost flex flex-col items-center gap-1 p-2 min-w-[56px] min-h-[56px] rounded-lg cursor-pointer",
              "transition-all duration-200 ease-out active:scale-95",
              "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none",
              view === item.view
                ? "text-primary bg-primary/5"
                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50/50"
            )}
          >
            <item.icon size={20} strokeWidth={view === item.view ? 2.5 : 2} aria-hidden="true" />
            <span className={cn(
              "text-[10px] font-medium uppercase tracking-wide",
              lang === 'fa' && 'font-farsi'
            )}>
              {item.label[lang]}
            </span>
          </button>
        ))}

        <a
          href="https://calendar.app.google/NbuyxdkKnzVU9gj17"
          target="_blank"
          rel="noopener noreferrer"
          aria-label={lang === 'en' ? 'Book a session (opens in new tab)' : 'رزرو جلسه (در تب جدید باز می‌شود)'}
          className={cn(
            "btn btn-ghost flex flex-col items-center gap-1 p-2 min-w-[56px] min-h-[56px] rounded-lg cursor-pointer",
            "text-primary bg-primary/5 transition-all duration-200 ease-out active:scale-95",
            "hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:outline-none"
          )}
        >
          <Calendar size={20} strokeWidth={2.5} aria-hidden="true" />
          <span className={cn(
            "text-[10px] font-medium uppercase tracking-wide",
            lang === 'fa' && 'font-farsi'
          )}>
            {lang === 'en' ? 'Book' : 'رزرو'}
          </span>
        </a>
      </nav>
    </>
  );
};

const App: React.FC = () => {
  const [lang, setLang] = useState<Language>('en');
  const [view, setView] = useState<View>('home');
  const prevViewRef = useRef<View>(view);
  const scrollTargetRef = useRef<string | null>(null);

  useEffect(() => {
    document.documentElement.dir = lang === 'fa' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    if (lang === 'fa') document.body.classList.add('font-farsi');
    else document.body.classList.remove('font-farsi');

    const viewChanged = prevViewRef.current !== view;
    prevViewRef.current = view;

    if (scrollTargetRef.current) {
      const targetId = scrollTargetRef.current;
      setTimeout(() => {
        const element = document.querySelector(targetId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
        scrollTargetRef.current = null;
      }, 600);
    } else if (viewChanged) {
      // Only scroll to top if the view actually changed
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [lang, view]);

  return (
    <div className="min-h-dvh bg-white text-brandDark selection:bg-primary selection:text-cream pb-20 md:pb-0 overflow-x-hidden">
      {/* Scroll Progress Indicator */}
      <ScrollProgress />

      <Nav lang={lang} setLang={setLang} view={view} setView={setView} />

      <main id="main-content">
        <AnimatePresence mode="wait">
          {view === 'home' && (
            <motion.div
              key="home"
              variants={pageStackVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="w-full"
            >
              <HomePage lang={lang} setView={setView} />
            </motion.div>
          )}

          {view === 'journey' && (
            <motion.div
              key="journey"
              variants={pageStackVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="w-full min-h-dvh"
            >
              <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-gray-500">Loading...</div></div>}>
                <JourneyPage lang={lang} setView={setView} />
              </Suspense>
            </motion.div>
          )}

          {view === 'how-i-help' && (
            <motion.div
              key="how-i-help"
              variants={pageStackVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <HowIHelpPage lang={lang} setView={setView} />
            </motion.div>
          )}

          {view === 'about' && (
            <motion.div
              key="about"
              variants={pageStackVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <AboutPage lang={lang} setView={setView} onBack={() => setView('home')} />
            </motion.div>
          )}

          {view === 'workshops' && (
             <motion.div
              key="workshops"
              variants={pageStackVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <WorkshopsPage lang={lang} onBack={() => setView('home')} />
            </motion.div>
          )}

          {view === 'impact' && (
             <motion.div
              key="impact"
              variants={pageStackVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ImpactPage lang={lang} setView={setView} onBack={() => setView('home')} />
            </motion.div>
          )}

          {view === 'approach' && (
             <motion.div
              key="approach"
              variants={pageStackVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ApproachPage lang={lang} setView={setView} onBack={() => setView('impact')} />
            </motion.div>
          )}

          {view === 'process' && (
             <motion.div
              key="process"
              variants={pageStackVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              <ProcessPage lang={lang} setView={setView} onBack={() => setView('impact')} />
            </motion.div>
          )}

        </AnimatePresence>
      </main>
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <Agentation />
        </Suspense>
      )}
    </div>
  );
};

export default App;
